(function () {
  'use strict';
  if (window.PartifyCollectionPurchase) return;
  const configNode = document.querySelector('[data-collection-purchase-config]');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const strings = config.strings;
  const money = new Intl.NumberFormat(config.locale || document.documentElement.lang || 'en-US', {
    style: 'currency', currency: config.currency || 'USD'
  });
  const states = new WeakMap();
  const requests = new Map();
  const queue = [];
  const formRequests = new Map();
  let activeRequests = 0;
  let activePurchase = null;
  let dialog;
  let successDialog;
  let successTrigger;
  const successDuration = 1500;
  const successExitDuration = 220;
  let successTimer;
  let successExitTimer;
  let successDeadline = 0;
  let successRemaining = successDuration;
  let successKeyboardActive = false;
  let successPointerOnAction = false;
  const vehiclePaint = window.PartifyVehiclePaintPreference;
  const unpainted = value => /^(unpainted|sin pintar)$/i.test(String(value).trim());
  const plain = value => !value || /^default title$/i.test(value);
  const finishKey = value => String(value || '').trim().toLowerCase();

  function activeVehicle() {
    try { return JSON.parse(localStorage.getItem('searchTerms') || '[]')[0] || null; } catch (_) { return null; }
  }

  function vehicleKey(vehicle = activeVehicle()) {
    if (!vehicle) return location.pathname;
    const vin = String(vehicle.vin || '').trim().toUpperCase();
    return /^[A-HJ-NPR-Z0-9]{17}$/.test(vin) ? 'vin:' + vin : ['ymm', vehicle.year, vehicle.make, vehicle.model].map(finishKey).join('|');
  }

  function paintCodes(value) {
    if (unpainted(value) || plain(value)) return [];
    const prefix = String(value || '').trim().split(/\s*[—–·]\s*|\s+-\s+/)[0];
    const code = prefix.match(/^[a-z0-9.-]+(?:\s*[/,|]\s*[a-z0-9.-]+)*/i)?.[0];
    if (!code || unpainted(code) || /^(\.vin|vin|unknown|n\/a|match|custom|two)$/i.test(code)) return [];
    return code.toUpperCase().split(/[/,|]/).map(value => value.trim()).filter(Boolean);
  }

  function preferredPaint() {
    const vehicle = activeVehicle();
    const selected = vehiclePaint?.selected(vehicle);
    if (selected) return selected;
    const vin = String(vehicle?.vin || '').trim();
    const plate = String(vehicle?.plate || vehicle?.licensePlate || '').trim();
    return (/^[A-HJ-NPR-Z0-9]{17}$/i.test(vin) || plate) && paintCodes(vehicle?.paintCode).length ? vehicle.paintCode : '';
  }

  function priceDelta(price, basePrice) {
    if (!Number.isFinite(price) || !Number.isFinite(basePrice)) return '';
    const difference = price - basePrice;
    return difference ? ' · ' + (difference > 0 ? '+' : '−') + money.format(Math.abs(difference) / 100) : '';
  }

  function matchingPaint(finish, preference) {
    if (vehiclePaint) return vehiclePaint.matches(finish, preference);
    return finishKey(finish) === finishKey(preference) || paintCodes(finish).some(code => paintCodes(preference).includes(code));
  }

  function referencedPaint(state, available, preference) {
    const vehicle = activeVehicle();
    if (state.manualPaint || vehiclePaint?.selected(vehicle) || !matchingPaint(preference, vehicle?.paintCode)) return null;
    const references = variant => {
      let values = variant.paintReferences || [];
      if (typeof values === 'string') {
        try { values = JSON.parse(values); } catch (_) {}
      }
      return (Array.isArray(values) ? values : [values]).flatMap(value => String(value || '').split(',')).map(finishKey).filter(Boolean);
    };
    const unique = candidates => {
      const finishes = new Set(candidates.map(variant => paintCodes(variant.finish).sort().join('/')));
      return finishes.size === 1 ? candidates[0] : null;
    };
    const rawCode = vehicle.troublesome_format || preference;
    const codeMatch = unique(available.filter(variant => references(variant).some(reference => matchingPaint(reference, rawCode))));
    if (codeMatch) return codeMatch;
    const description = finishKey(vehicle.troublesome_description);
    if (!description || finishKey(vehicle.make) === 'volkswagen') return null;
    return unique(available.filter(variant => references(variant).includes(description) &&
      (typeof isPaintCodeValidForVehicle !== 'function' || paintCodes(variant.finish).some(code => isPaintCodeValidForVehicle(vehicle, code) !== false))));
  }

  function selectPaint(state, preference) {
    const allowedIds = new Set(Array.from(state.paint.options).filter(option => !option.disabled).map(option => option.value));
    const available = state.group.variants.filter(variant => state.group.available && variant.available && !variant.hidden && allowedIds.has(String(variant.id)));
    const match = preference && (available.find(variant => matchingPaint(variant.finish, preference)) || referencedPaint(state, available, preference));
    const selection = match || available.find(variant => unpainted(variant.finish));
    state.paint.value = selection ? String(selection.id) : '';
    state.paintMismatch = paintCodes(preference).length && !match ? preference : '';
  }

  function applyPreferredPaint(panel) {
    const state = states.get(panel);
    if (!state?.data || !state.hasPaint || state.manualPaint || activePurchase?.panel === panel) return;
    selectPaint(state, preferredPaint());
    updateSelection(panel);
  }

  function rememberPaint(finish) {
    vehiclePaint?.remember(finish);
    document.querySelectorAll('[data-collection-purchase]').forEach(applyPreferredPaint);
  }

  function productUrl(panel) {
    const url = new URL(panel.dataset.productUrl, location.origin);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) throw new Error('Invalid product URL');
    const current = new URL(location.href);
    ['preview_theme_id', '_fd', 'pb'].forEach(key => {
      if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
    });
    return url;
  }

  function status(panel, text, error = false) {
    const node = panel.querySelector('[data-collection-status]');
    node.textContent = text;
    node.toggleAttribute('data-error', error);
  }

  function drain() {
    while (activeRequests < 2 && queue.length) {
      const job = queue.shift();
      activeRequests++;
      job.run().then(job.resolve, job.reject).finally(() => { activeRequests--; drain(); });
    }
  }

  function getData(panel) {
    const url = productUrl(panel);
    url.searchParams.set('view', 'collection-purchase-data');
    const key = url.href;
    if (!requests.has(key)) {
      requests.set(key, new Promise((resolve, reject) => {
        queue.push({ resolve, reject, run: async () => {
          const response = await fetch(key, { credentials: 'same-origin', signal: AbortSignal.timeout(10000) });
          if (!response.ok) throw new Error('Options unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const payload = doc.querySelector('[data-collection-purchase-json]');
          const data = payload && JSON.parse(payload.textContent);
          if (!data || String(data.id) !== panel.dataset.productId || !Array.isArray(data.groups)) throw new Error('Invalid product options');
          return data;
        } });
        drain();
      }).catch(error => { requests.delete(key); throw error; }));
    }
    return requests.get(key);
  }

  function selectVariant(state) {
    const group = state.group;
    return state.hasPaint
      ? group.variants.find(variant => String(variant.id) === state.paint.value)
      : group.variants.find(variant => String(variant.id) === String(group.baseId));
  }

  function updateSelection(panel) {
    const state = states.get(panel);
    const variant = selectVariant(state);
    const base = state.group.variants.find(item => String(item.id) === String(state.group.baseId));
    const pricedVariant = variant || base;
    panel.querySelector('[data-collection-purchase-price]').textContent = !state.group.available || pricedVariant?.available === false ? strings.soldOut : pricedVariant?.priceText || '';
    const ready = !!(state.group.available && variant?.available && !variant.hidden);
    state.add.disabled = !ready;
    state.add.textContent = state.group.available ? strings.add : strings.soldOut;
    if (state.baseTitle && state.title) {
      state.title.textContent = state.baseTitle + (state.group.key === 'capa' ? ' CAPA' : state.group.key === 'oem' ? ' OEM' : '') + (!state.bundleSkus && state.group.baseSku ? ' - ' + state.group.baseSku : '');
    }
    if (state.bundleSkus && state.group.baseSku && state.bundleSkus.dataset.sku !== state.group.baseSku) {
      state.bundleSkus.replaceChildren();
      state.group.baseSku.split('+').forEach((sku, index) => {
        if (index) { const separator = document.createElement('span'); separator.textContent = '+'; separator.setAttribute('aria-hidden', 'true'); state.bundleSkus.append(separator); }
        const value = document.createElement('span'); value.textContent = sku.trim(); state.bundleSkus.append(value);
      });
      state.bundleSkus.dataset.sku = state.group.baseSku;
    }
    const mismatchMessage = unpainted(variant?.finish) ? strings.unpaintedFallback : strings.paintUnavailable;
    status(panel, state.paintMismatch ? mismatchMessage.replace('[code]', String(state.paintMismatch).split(/[ —·]/)[0]) : '');
  }

  function updateQuality(panel, previousFinish = '') {
    const state = states.get(panel);
    state.group = state.data.groups.find(group => group.key === state.quality.value);
    const entries = state.group.variants.filter(variant => !variant.hidden && !/match\s+(?:paint\s+)?by\s+vin|pintura de combinación por vin|^\.vin\b|custom paint code|código de pintura personalizado/i.test(variant.finish));
    state.hasPaint = entries.some(variant => !unpainted(variant.finish) && !plain(variant.finish));
    state.paint.replaceChildren(new Option(strings.choosePaint, ''));
    state.paint.options[0].disabled = true;
    const base = state.group.variants.find(variant => String(variant.id) === String(state.group.baseId));
    entries.forEach(variant => {
      if (plain(variant.finish)) return;
      const label = unpainted(variant.finish) ? strings.unpainted : variant.finish;
      const available = state.group.available && variant.available;
      const option = new Option(label + (available ? priceDelta(variant.price, base?.price) : ' — ' + strings.soldOut), String(variant.id));
      option.disabled = !available;
      state.paint.appendChild(option);
    });
    state.paint.disabled = !state.group.available;
    panel.querySelector('[data-collection-paint-field]').hidden = !state.hasPaint;
    state.paintMismatch = '';
    if (state.hasPaint) selectPaint(state, previousFinish || preferredPaint());
    updateSelection(panel);
  }

  async function load(panel) {
    const state = states.get(panel);
    if (state.data || state.loading) return;
    state.loading = true;
    panel.querySelector('[data-collection-purchase-skeleton]').hidden = false;
    panel.setAttribute('data-loading', '');
    panel.setAttribute('aria-busy', 'true');
    status(panel, strings.loading);
    panel.querySelector('[data-collection-retry]').hidden = true;
    try {
      const data = await getData(panel);
      if (!panel.isConnected) return;
      if (!data.supported || !data.groups.length || data.groups.some(group => group.complete === false)) {
        panel.querySelector('[data-collection-purchase-skeleton]').hidden = true;
        status(panel, '');
        return;
      }
      state.data = data;
      state.title = panel.closest('[data-product-item]')?.querySelector('.productitem--title a');
      state.bundleSkus = panel.closest('[data-product-item]')?.querySelector('.productitem--bundle-skus-values');
      const originalTitle = state.title?.textContent.trim();
      const suffix = data.groups.map(group => group.baseSku && ' - ' + group.baseSku).find(value => value && originalTitle?.endsWith(value));
      if (suffix) state.baseTitle = originalTitle.slice(0, -suffix.length).replace(/\s+(CAPA(?: Certified)?|OEM)$/i, '');
      else if (state.bundleSkus && originalTitle) state.baseTitle = originalTitle.replace(/\s+(CAPA(?: Certified)?|OEM)$/i, '');
      state.quality.replaceChildren();
      const baseGroup = data.groups.find(group => group.available);
      const basePrice = baseGroup?.variants.find(variant => String(variant.id) === String(baseGroup.baseId))?.price;
      data.groups.forEach(group => {
        const groupPrice = group.variants.find(variant => String(variant.id) === String(group.baseId))?.price;
        state.quality.add(new Option((strings[group.key] || group.key) + (group.available ? priceDelta(groupPrice, basePrice) : ' — ' + strings.soldOut), group.key, false, false));
      });
      Array.from(state.quality.options).forEach((option, index) => { option.disabled = !data.groups[index].available; });
      state.quality.value = (data.groups.find(group => group.available) || data.groups[0]).key;
      state.quality.disabled = !data.groups.some(group => group.available);
      panel.querySelector('[data-collection-quality-field]').hidden = data.groups.length === 1 && data.groups[0].key === 'standard';
      panel.querySelector('[data-collection-purchase-fields]').hidden = false;
      updateQuality(panel);
      panel.querySelector('[data-collection-purchase-skeleton]').hidden = true;
    } catch (_) {
      state.data = null;
      state.quality.replaceChildren();
      panel.querySelector('[data-collection-purchase-fields]').hidden = true;
      panel.querySelector('[data-collection-purchase-skeleton]').hidden = true;
      status(panel, strings.loadError, true);
      panel.querySelector('[data-collection-retry]').hidden = false;
    } finally {
      state.loading = false;
      panel.removeAttribute('data-loading');
      panel.removeAttribute('aria-busy');
    }
  }

  function purchaseMarkup(panel) {
    const url = productUrl(panel);
    url.searchParams.set('view', 'collection-purchase');
    const key = url.href;
    if (!formRequests.has(key)) {
      if (formRequests.size >= 12) formRequests.delete(formRequests.keys().next().value);
      const request = fetch(key, { credentials: 'same-origin', signal: AbortSignal.timeout(10000) }).then(async response => {
        if (!response.ok) throw new Error('Purchase form request failed');
        const markup = await response.text();
        const doc = new DOMParser().parseFromString(markup, 'text/html');
        const form = doc.querySelector('[data-collection-purchase-document]');
        if (!form?.querySelector('#product-form')) throw new Error('Purchase form missing');
        return form.outerHTML;
      }).catch(error => { formRequests.delete(key); throw error; });
      formRequests.set(key, request);
    }
    return formRequests.get(key);
  }

  function warmPurchase(panel) {
    const state = states.get(panel);
    if (state?.data?.supported) purchaseMarkup(panel).catch(() => {});
  }

  function ensureDialog() {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'collection-purchase-dialog';
    dialog.setAttribute('aria-labelledby', 'collection-purchase-title');
    dialog.innerHTML = '<div class="collection-purchase-dialog__header"><h2 class="collection-purchase-dialog__title" id="collection-purchase-title"></h2><button type="button" class="collection-purchase-dialog__close" data-collection-close>×</button></div><div class="collection-purchase-dialog__body"><p class="collection-purchase-dialog__status" role="status"></p><a class="collection-purchase-dialog__details" hidden></a><div data-collection-purchase-content></div></div>';
    dialog.querySelector('h2').textContent = strings.title;
    dialog.querySelector('[data-collection-close]').setAttribute('aria-label', strings.close);
    dialog.querySelector('[data-collection-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('cancel', event => { if (activePurchase?.submitting) event.preventDefault(); });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog || activePurchase?.submitting) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    });
    dialog.addEventListener('close', () => {
      const purchase = activePurchase;
      if (!purchase) return;
      activePurchase = null;
      purchase.listenerController.abort();
      purchase.gate?.destroy();
      purchase.cardCleanup?.();
      window.__partifyCollectionAcqCleanup?.();
      window.__partifyCollectionAcqCleanup = null;
      if (purchase.gateGlobals) {
        Object.keys(window).filter(key => key.startsWith('__pdpFitmentGate')).forEach(key => { if (!(key in purchase.gateGlobals)) delete window[key]; });
        Object.assign(window, purchase.gateGlobals);
      }
      if (purchase.originals) Object.assign(window, purchase.originals);
      if (purchase.twoTone) document.documentElement.dataset.partifyTwoTonePaint = purchase.twoTone;
      else delete document.documentElement.dataset.partifyTwoTonePaint;
      dialog.querySelector('[data-collection-purchase-content]').replaceChildren();
      purchase.state.add.removeAttribute('aria-busy');
      updateSelection(purchase.panel);
      if (purchase.added) status(purchase.panel, strings.added);
      if (purchase.state.add.isConnected) purchase.state.add.focus({ preventScroll: true });
      if (purchase.added) showSuccess(purchase);
    });
    document.body.appendChild(dialog);
    return dialog;
  }

  function pauseSuccessTimer() {
    if (!successDialog?.open || successDialog.classList.contains('is-paused') || successDialog.classList.contains('is-closing')) return;
    clearTimeout(successTimer);
    successRemaining = Math.max(0, successDeadline - performance.now());
    successDialog.classList.add('is-paused');
  }

  function resumeSuccessTimer() {
    if (!successDialog?.open || !successDialog.classList.contains('is-paused') || successDialog.classList.contains('is-closing') || successKeyboardActive || document.hidden) return;
    successDialog.classList.remove('is-paused');
    successDeadline = performance.now() + successRemaining;
    successTimer = setTimeout(dismissSuccessDialog, successRemaining);
  }

  function dismissSuccessDialog() {
    if (!successDialog?.open || successDialog.classList.contains('is-closing')) return;
    clearTimeout(successTimer);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { successDialog.close(); return; }
    successDialog.classList.add('is-closing');
    successExitTimer = setTimeout(() => { if (successDialog.open) successDialog.close(); }, successExitDuration);
  }

  function startSuccessTimer() {
    clearTimeout(successTimer);
    clearTimeout(successExitTimer);
    successRemaining = successDuration;
    successKeyboardActive = false;
    successPointerOnAction = false;
    successDialog.classList.remove('is-counting', 'is-paused', 'is-closing');
    void successDialog.offsetWidth;
    successDialog.classList.add('is-counting');
    successDeadline = performance.now() + successDuration;
    successTimer = setTimeout(dismissSuccessDialog, successDuration);
    if (document.hidden) pauseSuccessTimer();
  }

  function trackSuccessPointer(event) {
    if (!successDialog?.open) return;
    successPointerOnAction = event.pointerType === 'mouse' && event.target instanceof Element && !!event.target.closest('button, a');
    if (successPointerOnAction) pauseSuccessTimer();
    else resumeSuccessTimer();
  }

  function ensureSuccessDialog() {
    if (successDialog) return successDialog;
    successDialog = document.createElement('dialog');
    successDialog.className = 'collection-purchase-success';
    successDialog.setAttribute('aria-labelledby', 'collection-purchase-success-title');
    successDialog.innerHTML = '<button type="button" class="collection-purchase-success__close" data-success-close>×</button><span class="collection-purchase-success__check" aria-hidden="true"><svg class="collection-purchase-success__ring" viewBox="0 0 64 64"><circle class="collection-purchase-success__ring-track" cx="32" cy="32" r="29"/><circle class="collection-purchase-success__ring-progress" cx="32" cy="32" r="29"/></svg><span>✓</span></span><h2 id="collection-purchase-success-title" tabindex="-1"></h2><p class="collection-purchase-success__product"></p><div class="collection-purchase-success__actions"><a class="collection-purchase-success__cart"></a><button type="button" class="collection-purchase-success__continue" data-success-close></button></div>';
    successDialog.querySelector('.collection-purchase-success__close').setAttribute('aria-label', strings.close);
    successDialog.querySelectorAll('[data-success-close]').forEach(button => button.addEventListener('click', dismissSuccessDialog));
    successDialog.addEventListener('cancel', event => { event.preventDefault(); dismissSuccessDialog(); });
    successDialog.addEventListener('click', event => {
      if (event.target !== successDialog) return;
      const rect = successDialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dismissSuccessDialog();
    });
    successDialog.addEventListener('pointermove', trackSuccessPointer);
    successDialog.addEventListener('pointerdown', trackSuccessPointer);
    successDialog.addEventListener('pointerleave', () => { successPointerOnAction = false; resumeSuccessTimer(); });
    successDialog.addEventListener('keydown', () => { successKeyboardActive = true; pauseSuccessTimer(); });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) pauseSuccessTimer();
      else if (!successPointerOnAction) resumeSuccessTimer();
    });
    successDialog.addEventListener('close', () => {
      clearTimeout(successTimer);
      clearTimeout(successExitTimer);
      successDialog.classList.remove('is-counting', 'is-paused', 'is-closing');
      if (successTrigger?.isConnected) successTrigger.focus({ preventScroll: true });
      successTrigger = null;
    });
    document.body.appendChild(successDialog);
    return successDialog;
  }

  function showSuccess(purchase) {
    const modal = ensureSuccessDialog();
    modal.querySelector('h2').textContent = strings.added;
    modal.querySelector('.collection-purchase-success__product').textContent = purchase.panel.closest('[data-product-item]')?.querySelector('.productitem--title a')?.textContent.trim() || '';
    const cart = modal.querySelector('.collection-purchase-success__cart');
    cart.href = (window.Shopify?.routes?.root || '/') + 'cart';
    cart.textContent = strings.viewCart;
    modal.querySelector('.collection-purchase-success__continue').textContent = strings.continueShopping || strings.close;
    successTrigger = purchase.state.add;
    modal.showModal();
    modal.querySelector('h2').focus({ preventScroll: true });
    startSuccessTimer();
  }

  function hasCustomQuestions(content) {
    return !!content.querySelector('#fitment-questions-root .fitment-type-select, [data-country-origin-fitment-root] .country-origin-fitment, #fitment-gate-mount .country-origin-fitment');
  }

  function purchaseError(purchase, message = strings.prepareError) {
    if (activePurchase !== purchase) return;
    dialog.removeAttribute('data-loading');
    if (dialog.hasAttribute('data-quick-add')) {
      dialog.removeAttribute('data-quick-add');
      dialog.querySelector('h2').textContent = strings.title;
    }
    const node = dialog.querySelector('[role="status"]');
    node.hidden = false;
    node.textContent = message;
    dialog.querySelector('.collection-purchase-dialog__details').hidden = false;
    if (!purchase.ready) dialog.querySelector('[data-collection-purchase-content]').hidden = true;
  }

  async function added(purchase) {
    if (activePurchase !== purchase || purchase.added) return;
    purchase.added = true;
    vehiclePaint?.confirm(purchase.paintContext);
    const root = window.Shopify?.routes?.root || '/';
    dialog.close();
    if (!purchase.panel.querySelector('.collection-purchase__cart')) {
      const link = document.createElement('a');
      link.className = 'collection-purchase__cart';
      link.href = root + 'cart';
      link.textContent = strings.viewCart;
      purchase.panel.appendChild(link);
    }
    try {
      const response = await fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(8000) });
      if (response.ok) {
        const cart = await response.json();
        document.querySelectorAll('[data-header-cart-count]').forEach(node => {
          node.dataset.headerCartCount = String(cart.item_count);
          node.classList.toggle('visible', cart.item_count > 0);
        });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      }
    } catch (_) {}
  }

  function vehicleProperties() {
    try {
      const vehicle = JSON.parse(localStorage.getItem('searchTerms') || '[]')[0];
      if (!vehicle) return {};
      const fields = [vehicle.year, vehicle.make, vehicle.model, vehicle.submodel, vehicle.engine].filter(Boolean);
      return fields.length ? { Fits: fields.join(' / ') } : {};
    } catch (_) { return {}; }
  }

  async function editSubmodel(purchase, card) {
    if (purchase.submitting || activePurchase !== purchase) return;
    const vehicle = activeVehicle();
    if (!vehicle || !window.PartifyYMM?.getVehicleOptions) return;
    const content = dialog.querySelector('[data-collection-purchase-content]');
    content.querySelector('[data-collection-vehicle-editor]')?.remove();
    const editor = document.createElement('div');
    editor.className = 'collection-purchase__vehicle-editor';
    editor.setAttribute('data-collection-vehicle-editor', '');
    editor.innerHTML = '<p role="status"></p><div data-collection-vehicle-fields hidden><label><span></span><select data-collection-submodel required></select></label><label><span></span><select data-collection-engine></select></label></div><div class="collection-purchase__vehicle-actions"><button type="button" data-collection-vehicle-save disabled></button><button type="button" data-collection-vehicle-retry hidden></button><button type="button" data-collection-vehicle-cancel></button></div>';
    card.after(editor);
    const message = editor.querySelector('[role="status"]');
    message.textContent = strings.vehicleLoading;
    const submodel = editor.querySelector('[data-collection-submodel]');
    const engine = editor.querySelector('[data-collection-engine]');
    submodel.previousElementSibling.textContent = strings.submodel;
    engine.previousElementSibling.textContent = strings.engine;
    const save = editor.querySelector('[data-collection-vehicle-save]');
    save.textContent = strings.saveVehicle;
    const retry = editor.querySelector('[data-collection-vehicle-retry]');
    retry.textContent = strings.retry;
    retry.addEventListener('click', () => editSubmodel(purchase, card));
    const cancel = editor.querySelector('[data-collection-vehicle-cancel]');
    cancel.textContent = strings.cancel;
    cancel.addEventListener('click', () => { editor.remove(); card.querySelector('[data-pfc-change]')?.focus(); });
    try {
      const options = await window.PartifyYMM.getVehicleOptions(vehicle);
      if (activePurchase !== purchase || !editor.isConnected) return;
      if (!options.submodels.length) throw new Error('Submodels unavailable');
      submodel.add(new Option(strings.chooseSubmodel, ''));
      options.submodels.forEach(value => submodel.add(new Option(value, value)));
      submodel.value = vehicle.submodel || '';
      const fillEngines = () => {
        const previous = engine.value || vehicle.engine;
        engine.replaceChildren(new Option(strings.engine, ''));
        (options.engines[submodel.value] || []).forEach(value => engine.add(new Option(value, value)));
        if (Array.from(engine.options).some(option => option.value === previous)) engine.value = previous;
        engine.disabled = engine.options.length === 1;
        save.disabled = !submodel.value;
      };
      submodel.addEventListener('change', fillEngines);
      fillEngines();
      message.textContent = strings.vehicleHint;
      editor.querySelector('[data-collection-vehicle-fields]').hidden = false;
      save.addEventListener('click', () => {
        if (!submodel.reportValidity() || vehicleKey() !== vehicleKey(vehicle)) return;
        const terms = JSON.parse(localStorage.getItem('searchTerms') || '[]');
        const updated = { ...terms[0], submodel: submodel.value, engine: engine.value };
        updated.ymm = [updated.year, updated.make, updated.model, updated.submodel, updated.engine].filter(Boolean).join(' ');
        updated.fitmentYmm = updated.ymm;
        const fitment = { value: [updated.year, updated.make, updated.model, updated.submodel, updated.engine].filter(Boolean).map(value => value + 'easysearch-preselect-delimiter').join(''), expires: String(Date.now() + 365 * 86400000) };
        if (window.setFitmentObject) window.setFitmentObject(fitment);
        else localStorage.setItem('easysearch-preselect-fitment', JSON.stringify(fitment));
        terms[0] = updated;
        if (window.setSearchTerms) window.setSearchTerms(terms, 'collection-purchase');
        else { localStorage.setItem('searchTerms', JSON.stringify(terms)); window.dispatchEvent(new Event('searchTermsUpdated')); }
        window.PartifyFitment?.run();
        window.PartifyFitmentCard?.refresh();
        editor.remove();
        card.querySelector('[data-pfc-change]')?.focus();
      });
      submodel.focus({ preventScroll: true });
      editor.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch (_) {
      if (activePurchase !== purchase || !editor.isConnected) return;
      message.textContent = strings.vehicleError;
      retry.hidden = false;
    }
  }

  async function submitPurchase(purchase) {
    if (activePurchase !== purchase || purchase.submitting || purchase.added) return;
    const state = purchase.state;
    const variant = selectVariant(state);
    if (!variant?.available || !state.group.available || String(variant.id) !== String(purchase.variant.id)) return;
    const form = purchase.form;
    if (!form.reportValidity()) return;
    const country = window.PartifyCountryOriginFitment?.getVerifiedVinRequirement?.() || { applies: false };
    const vinContext = window.PartifyVinCartGuard?.resolveProductVinContext(form) || {};
    const enteredVin = form.querySelector('[name="properties[VIN]"]')?.value?.trim().toUpperCase();
    if (vinContext.conflict) { purchaseError(purchase, strings.vinConflict); return; }
    const vin = country.applies ? country.vin : enteredVin || vinContext.vin;
    if ((purchase.vinRequired || country.applies) && !/^[A-HJ-NPR-Z0-9]{17}$/.test(vin || '')) { purchaseError(purchase, strings.vinRequired); return; }
    if (country.applies && vinContext.vin && country.vin !== vinContext.vin) { purchaseError(purchase, strings.vinConflict); return; }
    const properties = { ...vehicleProperties(), ...window.__pdpFitmentGateGetReviewProperties?.() };
    new FormData(form).forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && String(value).trim()) properties[match[1]] = String(value).trim();
    });
    if (vin) properties.VIN = vin; else delete properties.VIN;
    if (country.country) properties['Manufacturing Country'] = country.country;
    if (variant.disclaimer) properties['Axalta disclaimer'] = 'Acknowledged';
    purchase.hasQuestions = hasCustomQuestions(dialog.querySelector('[data-collection-purchase-content]'));
    purchase.submitting = true;
    // Keep the answers mounted for retries, but show the compact pending popup immediately.
    dialog.setAttribute('data-quick-add', '');
    const heading = dialog.querySelector('h2');
    heading.textContent = strings.adding;
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
    dialog.querySelector('[data-collection-close]').disabled = true;
    const message = dialog.querySelector('[role="status"]');
    message.hidden = false;
    message.textContent = strings.adding;
    form.querySelectorAll('button[type="submit"]').forEach(button => { button.disabled = true; });
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: String(variant.id), quantity: 1, properties }] }) });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : strings.addError);
      await added(purchase);
    } catch (error) {
      if (activePurchase === purchase) {
        purchaseError(purchase, error.message);
        form.hidden = false;
        form.querySelectorAll('button[type="submit"]').forEach(button => { button.disabled = false; });
      }
    } finally { purchase.submitting = false; dialog.querySelector('[data-collection-close]').disabled = false; }
  }

  async function openPurchase(panel) {
    if (activePurchase) return;
    const state = states.get(panel);
    const variant = selectVariant(state);
    if (state.add.disabled || !variant?.available || !state.group.available) return;
    const modal = ensureDialog();
    const purchase = { panel, state, variant, paintContext: vehiclePaint?.capture(variant.finish), listenerController: new AbortController(), submitting: false, added: false, openedAt: performance.now() };
    activePurchase = purchase;
    state.add.setAttribute('aria-busy', 'true');
    modal.removeAttribute('data-quick-add');
    modal.querySelector('h2').textContent = strings.title;
    modal.setAttribute('data-loading', '');
    const message = modal.querySelector('[role="status"]');
    message.textContent = strings.prepare;
    message.hidden = false;
    const details = modal.querySelector('.collection-purchase-dialog__details');
    const detailsUrl = productUrl(panel);
    detailsUrl.searchParams.set('variant', String(variant.id));
    if (state.group.key !== 'standard') detailsUrl.searchParams.set('quality', state.group.key);
    details.href = detailsUrl.href;
    details.textContent = panel.querySelector('[data-collection-details]').textContent;
    details.hidden = true;
    modal.showModal();
    try {
      const markup = await purchaseMarkup(panel);
      if (activePurchase !== purchase) return;
      if (!window.PartifyPdpFitmentGate) throw new Error('Fitment controller unavailable');
      const globals = ['__collectionPurchaseRoot', '__pdpFitmentGate', '__PARTIFY_PRODUCT_COLLECTIONS', '__partifyNativeFitmentLastResult', 'PartifyCountryOriginFitment', 'PartifyVinBodyTypeAcq', '__partifyCountryOriginRestoreBound', '__partifyCountryOriginHistoryRestored', '__partifyCountryOriginRestorePending', 'activateExclusionUrl'];
      purchase.originals = Object.fromEntries(globals.map(key => [key, window[key]]));
      purchase.gateGlobals = Object.fromEntries(Object.keys(window).filter(key => key.startsWith('__pdpFitmentGate')).map(key => [key, window[key]]));
      purchase.twoTone = document.documentElement.dataset.partifyTwoTonePaint;
      window.__collectionPurchaseRoot = true;
      window.__pdpFitmentGate = true;
      const content = modal.querySelector('[data-collection-purchase-content]');
      content.hidden = false;
      content.innerHTML = markup;
      content.addEventListener('click', event => {
        if (!event.target.closest('[data-pfc-change]')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const needsSubmodel = content.querySelector('[data-partify-fitment-card]')?.dataset.pfcState === 'warn-submodel';
        if (needsSubmodel && window.PartifyYMM?.getVehicleOptions) {
          editSubmodel(purchase, content.querySelector('[data-partify-fitment-card]'));
          return;
        }
        modal.close();
        requestAnimationFrame(() => {
          if (needsSubmodel && window.GarageV2?.openAndFocusSubmodel) window.GarageV2.openAndFocusSubmodel();
          else if (window.GarageV2?.open) window.GarageV2.open();
          else document.getElementById('easyBtn')?.click();
        });
      }, { capture: true, signal: purchase.listenerController.signal });
      purchase.form = content.querySelector('#product-form');
      if (!purchase.form) throw new Error('Purchase form missing');
      purchase.form.querySelector('[name="id"]').value = String(variant.id);
      const fitmentConfig = JSON.parse(content.querySelector('[data-collection-fitment-config]')?.textContent || '{}');
      window.__PARTIFY_PRODUCT_COLLECTIONS = fitmentConfig.collections || [];
      content.querySelectorAll('[data-collection-fitment-markup] script').forEach(inert => {
        const script = document.createElement('script');
        script.textContent = inert.textContent;
        inert.replaceWith(script);
      });
      if (content.querySelector('#fitment-questions-root')?.dataset.fitmentReady !== 'true') throw new Error('Fitment questions unavailable');
      purchase.cardCleanup = window.PartifyFitmentCard?.mount();
      window.PartifyFitment?.run();
      purchase.gate = window.PartifyPdpFitmentGate.mount({ onDismiss: () => modal.close() });
      purchase.vinRequired = (fitmentConfig.paintCodes || []).some(code => String(code.paint_code || '').toUpperCase() === String(variant.finish || '').split(/[ —·]/)[0].toUpperCase() && code.vin_required === true);
      const paintNote = content.querySelector('[data-collection-paint-note]');
      if (paintNote) paintNote.hidden = !variant.disclaimer;
      const vinField = content.querySelector('.collection-purchase__vin');
      if (vinField) {
        vinField.hidden = !purchase.vinRequired;
        vinField.querySelector('input').required = purchase.vinRequired;
      }
      purchase.form.addEventListener('submit', event => { event.preventDefault(); submitPurchase(purchase); });
      purchase.ready = true;
      modal.removeAttribute('data-loading');
      modal.dataset.readyMs = String(Math.round(performance.now() - purchase.openedAt));
      message.hidden = true;
      const submit = () => { purchase.form.hidden = false; return submitPurchase(purchase); };
      if (window.__pdpFitmentGateRequestResolution(submit)) {
        purchase.form.hidden = true;
        modal.querySelector('h2').textContent = content.querySelector('#fitment-gate-title')?.textContent || strings.title;
        modal.querySelector('h2').tabIndex = -1;
        modal.querySelector('h2').focus({ preventScroll: true });
      } else if (!purchase.vinRequired) submit();
    } catch (error) {
      if (activePurchase === purchase) console.warn('[Collection purchase]', error.message);
      purchaseError(purchase);
    }
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) { observer.unobserve(entry.target); load(entry.target); } });
  }, { rootMargin: '100px' }) : null;

  function scan() {
    document.querySelectorAll('[data-collection-purchase]').forEach(panel => {
      if (states.has(panel)) return;
      const state = { quality: panel.querySelector('[data-collection-quality]'), paint: panel.querySelector('[data-collection-paint]'), add: panel.querySelector('[data-collection-add]'), data: null, loading: false };
      states.set(panel, state);
      state.quality.addEventListener('change', () => {
        const previous = selectVariant(state);
        const preference = state.manualPaint ? state.manualFinish : preferredPaint();
        updateQuality(panel, preference || previous?.finish || '');
      });
      state.paint.addEventListener('change', () => {
        state.manualPaint = true;
        state.paintMismatch = '';
        updateSelection(panel);
        const variant = selectVariant(state);
        state.manualFinish = variant?.finish || '';
        if (variant?.available && !unpainted(variant.finish)) rememberPaint(variant.finish);
        state.manualPreference = preferredPaint();
        warmPurchase(panel);
      });
      state.add.addEventListener('pointerenter', () => warmPurchase(panel), { once: true });
      state.add.addEventListener('focus', () => warmPurchase(panel));
      state.add.addEventListener('click', () => openPurchase(panel));
      panel.querySelector('[data-collection-retry]').addEventListener('click', () => load(panel));
      panel.addEventListener('focusin', () => load(panel));
      if (observer) observer.observe(panel); else load(panel);
    });
  }
  window.PartifyCollectionPurchase = { scan };
  let scanQueued = false;
  new MutationObserver(() => {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => { scanQueued = false; scan(); });
  }).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', scan);
  let currentVehicleKey = vehicleKey();
  function syncVehiclePaint(event) {
    const scope = vehicleKey();
    const changed = scope !== currentVehicleKey;
    const preference = preferredPaint();
    const restored = ['pageshow', 'storage'].includes(event?.type) || event?.detail?.source === 'account-hydration';
    currentVehicleKey = scope;
    document.querySelectorAll('[data-collection-purchase]').forEach(panel => {
      const state = states.get(panel);
      if (!state?.data) return;
      if (changed || (restored && state.manualPreference !== preference)) { state.manualPaint = false; state.manualFinish = ''; state.paintMismatch = ''; }
      applyPreferredPaint(panel);
    });
  }
  window.addEventListener('searchTermsUpdated', syncVehiclePaint);
  window.addEventListener('partify:vehicle:changed', syncVehiclePaint);
  window.addEventListener('partify:paint-preference:changed', syncVehiclePaint);
  window.addEventListener('pageshow', syncVehiclePaint);
  window.addEventListener('storage', event => {
    if (event.key === 'searchTerms') syncVehiclePaint();
  });
  scan();
})();
